/**
 * LocationSessionProvider — holds the selected {group, teacher} identity for
 * a shared per-location account (room iPad). No-op passthrough for every
 * other viewer kind: normal staff/manager/coach/admin auth is untouched,
 * `isLocationViewer` is simply false and storage is never read.
 *
 * Selection is client-side state, persisted in localStorage (key
 * STORAGE_KEY) so a reload — or an iPadOS tab/PWA discard, which silently
 * wipes sessionStorage but not localStorage — doesn't drop the picked
 * identity mid-shift. The persisted payload is bound to the locationCode it
 * was written under: a device re-provisioned to a DIFFERENT location
 * account (without going through the GroupPicker sign-out) must NOT restore
 * the old location's group/teacher, so restore rejects (and clears) any
 * stored payload whose locationCode doesn't match the current viewer's —
 * including legacy payloads written before locationCode existed, which
 * always mismatch and get a one-time re-pick (owner-accepted hardening).
 * The old sessionStorage key from before the localStorage swap (Task 8) is
 * likewise always rejected on read, since it never carried locationCode.
 * Every transition additionally writes an append-only
 * public.location_session_events row via locationSessionService — insert
 * failures throw, never swallowed (Task 2.1,
 * docs/plans/2026-07-30-location-login.md).
 */

import { useCallback, useMemo, useState, type ReactNode } from 'react';

import { useBootContext } from '@/hooks/useBootContext';

import { LocationSessionContext } from './context';
import { locationSessionService } from './services/locationSessionService';
import type {
  LocationSessionContextValue,
  LocationSessionGroup,
  LocationSessionState,
  LocationSessionTeacher,
} from './types';

export const STORAGE_KEY = 'tk-location-session';

/**
 * Storage is untrusted input (hand-editable) — validate the full shape
 * before trusting it. `expectedLocationCode` binds the check to the
 * CURRENT viewer's location: a payload for a different (or missing)
 * locationCode is rejected outright, even if otherwise well-formed.
 */
function isValidStoredSession(value: unknown, expectedLocationCode: string): value is LocationSessionState {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  if (v.locationCode !== expectedLocationCode) return false;
  const group = v.group as Record<string, unknown> | undefined;
  // group.locationCode is required: payloads persisted before the sibling
  // pack (#1282) carry a group without it, which crashes RelockSheet's
  // sibling-badge render (`g.locationCode.toUpperCase()`).
  if (
    !group ||
    typeof group.id !== 'string' ||
    typeof group.name !== 'string' ||
    typeof group.locationCode !== 'string'
  ) {
    return false;
  }
  if (v.teacher === null) return true;
  const teacher = v.teacher as Record<string, unknown> | undefined;
  return (
    !!teacher &&
    typeof teacher.staffId === 'string' &&
    typeof teacher.firstName === 'string' &&
    typeof teacher.fullName === 'string'
  );
}

function readStoredSession(locationCode: string): LocationSessionState | null {
  const raw = localStorage.getItem(STORAGE_KEY);
  if (raw !== null) {
    let parsed: unknown;
    try {
      parsed = JSON.parse(raw);
    } catch {
      localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    // Returns a clean {group, teacher} — never the raw parsed object, whose
    // extra `locationCode` field belongs to the storage payload, not the
    // in-memory session shape.
    if (isValidStoredSession(parsed, locationCode)) return { group: parsed.group, teacher: parsed.teacher };
    // Malformed, or bound to a different location (device re-provisioned
    // without the GroupPicker sign-out path) — never restore across a
    // location boundary. Clear the stale key rather than leaving it to be
    // re-checked (and re-rejected) on every future restore.
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }

  // The legacy sessionStorage key (pre-localStorage-swap, Task 8) never
  // carried locationCode, so it always fails the check above anyway — just
  // clear it so this branch is dead after the first read on a device.
  sessionStorage.removeItem(STORAGE_KEY);
  return null;
}

export function LocationSessionProvider({ children }: { children: ReactNode }): JSX.Element {
  // Viewer kind is a selector over the boot query (useBootContext): the
  // caller's OWN active location_accounts code, resolved by auth.uid() in
  // app_boot_context() — checked independently of the staff row, so a
  // location iPad (no staff_master row) is a location viewer, and a
  // manager (who may SELECT every location account) never is. Same rule
  // as rolesService.getViewerContext's first branch, which hub-scope
  // callers still use for their location lists.
  const { query: bootQ } = useBootContext();

  const locationCode = bootQ.data?.locationCode ?? null;
  const isLocationViewer = !!locationCode;
  // Viewer-kind resolution isn't done until the boot query has DATA — while
  // auth resolves, while pending (a disabled query is "pending", not "done";
  // same gotcha useCurrentUserStaff documents) and after a failed boot with
  // no prior result. Consumers (LocationGate, route allowlist, nav) must not
  // treat the default isLocationViewer:false as a real answer while this is
  // true: an unknown status is never "not a location account", so a failed
  // boot never opens the app (LocationGate shows a retry instead).
  const isLoading = bootQ.data === undefined;

  const [session, setSession] = useState<LocationSessionState | null>(null);
  // Tracks the last isLocationViewer value the restore ran for — held in
  // STATE, not a ref, deliberately. A ref mutation from a render that never
  // commits (isLocationViewer flips true while the viewer query is still
  // settling, e.g. mid-transition) survives even though that render's
  // setSession(restored) gets thrown away, permanently marking the restore
  // "done" without ever having set the session (the bug this fixed:
  // restoredFor stayed a ref, isLocationViewer flipped true on a discarded
  // render, hasRestoredRef.current=true stuck around, the later committed
  // render then skipped restore entirely). State doesn't have this problem:
  // a discarded render's setRestoredFor(true) is discarded together with
  // its setSession(restored) — they can only ever commit as a pair.
  const [restoredFor, setRestoredFor] = useState(false);

  // Restore synchronously during render, the instant isLocationViewer is
  // known true — never read sessionStorage for a normal staff/manager/
  // coach/admin viewer. An effect-based restore leaves a one-render-frame
  // gap where isLocationViewer is already true but session is still null,
  // which LocationGate would render as the group picker even though a
  // valid session is about to restore (Phase 2 T3 finding). This is React's
  // documented "adjust state during render" pattern.
  if (isLocationViewer && !restoredFor) {
    setRestoredFor(true);
    // isLocationViewer only ever flips true once locationCode is resolved
    // (see the derivation above) — null here would be the same broken
    // invariant LocationGate already throws loudly on.
    if (locationCode) {
      const restored = readStoredSession(locationCode);
      if (restored) setSession(restored);
    }
  }

  const persist = useCallback((code: string, next: LocationSessionState | null) => {
    setSession(next);
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify({ locationCode: code, ...next }));
    else localStorage.removeItem(STORAGE_KEY);
  }, []);

  const requireLocationCode = useCallback((): string => {
    if (!locationCode) throw new Error('LocationSessionProvider: not a location viewer');
    return locationCode;
  }, [locationCode]);

  const [switchOpen, setSwitchOpen] = useState(false);
  // Ephemeral chip label for the group on screen — never persisted, never audited.
  const [viewingGroupName, setViewingGroupName] = useState<string | null>(null);
  // The teacher just cleared by relock() — powers the idle sheet's one-tap
  // "Still {name}? Continue" quick re-entry (Task 7).
  const [lastTeacher, setLastTeacher] = useState<LocationSessionTeacher | null>(null);

  const select = useCallback(
    async (group: LocationSessionGroup, teacher: LocationSessionTeacher) => {
      const code = requireLocationCode();
      // A prior live teacher means this commit came from the in-modal
      // switch-teacher flow (⇄ chip) — audit it distinctly from a plain
      // first-time/post-relock pick. Derived from state, not a caller flag,
      // so this stays the single source of truth for the event type.
      const event = session?.teacher ? 'switched' : 'selected';
      await locationSessionService.recordEvent({ event, locationCode: code, group, teacher });
      persist(code, { group, teacher });
      // 'switched' means the ⇄ flow picked someone else — the quick-continue
      // affordance no longer applies to whoever relock() had last cleared.
      if (event === 'switched') setLastTeacher(null);
      setSwitchOpen(false);
    },
    [requireLocationCode, persist, session],
  );

  const relock = useCallback(async () => {
    const code = requireLocationCode();
    if (!session) throw new Error('LocationSessionProvider: relock() with no active group');
    await locationSessionService.recordEvent({ event: 'relocked', locationCode: code, group: session.group, teacher: null });
    setLastTeacher(session.teacher);
    persist(code, { group: session.group, teacher: null });
  }, [requireLocationCode, persist, session]);

  const reset = useCallback(async () => {
    const code = requireLocationCode();
    await locationSessionService.recordEvent({ event: 'relocked', locationCode: code, group: null, teacher: null });
    setLastTeacher(null);
    persist(code, null);
  }, [requireLocationCode, persist]);

  const openSwitch = useCallback(() => setSwitchOpen(true), []);
  const closeSwitch = useCallback(() => setSwitchOpen(false), []);

  const value = useMemo<LocationSessionContextValue>(
    () => ({ isLocationViewer, isLoading, locationCode, session, lastTeacher, select, relock, reset, switchOpen, openSwitch, closeSwitch, viewingGroupName, setViewingGroupName }),
    [isLocationViewer, isLoading, locationCode, session, lastTeacher, select, relock, reset, switchOpen, openSwitch, closeSwitch, viewingGroupName],
  );

  return <LocationSessionContext.Provider value={value}>{children}</LocationSessionContext.Provider>;
}
